import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/errors";
import { GoogleG } from "../../components/GoogleG";

export default function Login() {
  const { user, loading, loginWithPassword, loginWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return <div className="py-24 text-center text-stone-500">Loading…</div>;
  if (user) return <Navigate to={user.is_admin ? "/admin" : "/account"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await loginWithPassword(email.trim(), password);
      window.location.href = u.is_admin ? "/admin" : "/account";
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="relative min-h-[70vh]">
      <div className="blob blob-gold" style={{ width: 500, height: 500, top: 40, right: -180 }}/>
      <div className="blob blob-terra" style={{ width: 400, height: 400, bottom: 100, left: -140 }}/>
      <div className="max-w-md mx-auto px-6 py-16 relative">
        <div className="text-center mb-8">
          <div className="uppercase tracking-[0.3em] text-xs text-[#8B7355] mb-3">Welcome back</div>
          <h1 className="font-serif-display text-5xl text-[#3B2F2F]">Sign in</h1>
          <p className="text-sm text-stone-500 mt-2">Your daily dose of good coffee, one login away.</p>
        </div>

        <form onSubmit={submit} className="bg-white/80 backdrop-blur rounded-3xl border border-stone-200 shadow-xl p-8 space-y-4" data-testid="login-form">
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500">Email</label>
            <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                   data-testid="login-email"
                   className="mt-1 w-full bg-stone-50 border border-stone-200 rounded-full px-4 py-3 outline-none focus:border-[#3B2F2F]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500">Password</label>
            <input type="password" required value={password} onChange={(e)=>setPassword(e.target.value)}
                   data-testid="login-password"
                   className="mt-1 w-full bg-stone-50 border border-stone-200 rounded-full px-4 py-3 outline-none focus:border-[#3B2F2F]" />
          </div>
          {err && <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{err}</div>}
          <button type="submit" disabled={busy} data-testid="login-submit"
                  className="w-full bg-[#3B2F2F] text-[#FDFBF7] rounded-full py-3 font-semibold hover:bg-black disabled:opacity-60">
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <div className="flex items-center justify-between text-xs">
            <Link to="/forgot-password" data-testid="link-forgot" className="text-[#8B7355] hover:text-[#3B2F2F]">Forgot password?</Link>
            <Link to="/register" data-testid="link-register" className="text-[#8B7355] hover:text-[#3B2F2F]">Create account →</Link>
          </div>

          <div className="relative py-2 flex items-center gap-3 text-xs text-stone-400">
            <div className="flex-1 border-t border-stone-200"/> or <div className="flex-1 border-t border-stone-200"/>
          </div>

          <button type="button" onClick={loginWithGoogle} data-testid="login-google"
                  className="w-full border border-stone-300 rounded-full py-3 text-sm font-medium hover:bg-stone-50 flex items-center justify-center gap-2">
            <GoogleG/> Continue with Google
          </button>

          <Link to="/login/phone" data-testid="login-phone-link"
                className="w-full border border-stone-300 rounded-full py-3 text-sm font-medium hover:bg-stone-50 flex items-center justify-center gap-2 text-stone-700">
            Sign in with mobile OTP
          </Link>

          <Link to="/wholesale/login" data-testid="login-wholesale-link"
                className="w-full bg-[#3B2F2F]/5 border border-[#D4AF37]/40 rounded-full py-3 text-sm font-medium hover:bg-[#FEF3E2] flex items-center justify-center gap-2 text-[#3B2F2F]">
            <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">B2B</span>
            Sign in as seller
          </Link>
        </form>

        <p className="text-center text-xs text-stone-400 mt-4">By signing in you agree to our <Link to="/terms" className="underline">Terms</Link> &amp; <Link to="/privacy" className="underline">Privacy</Link>.</p>
      </div>
    </div>
  );
}
